import React, { useMemo } from "react";
import { ContentPasteGo } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import { useNotification } from "@context/NotificationContext";
import { validateVesselCertificates } from "@features/vessel-call/create/utils/validateCertificates";
import type { PortCode } from "@services/vessel-call/types";
import type { Vessel } from "@services/vessel/types";
import type { UpdateVesselCallPayload, VesselCallResponse } from "@services/vesselCall/types";
import { updateVesselCallById } from "@services/vesselCallApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface UpdateVesselCallSectionProps {
  vesselCallDetails?: VesselCallResponse;
  vesselRegisterDetails?: Vessel | null;
  ports: PortCode[];
  refetchVesselCall: () => Promise<unknown>;
  onSubmit?: (data: UpdateVesselCallForm) => void;
  onCancel?: () => void;
}

interface UpdateVesselCallForm {
  eta?: string;
  expectedArrivalLocation?: string;
}

const LANG_BASE = "vessel-call.update-section";

const UpdateVesselCallSection: React.FC<UpdateVesselCallSectionProps> = ({
  vesselCallDetails,
  vesselRegisterDetails,
  ports,
  refetchVesselCall,
  onSubmit: onUpdateSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const form = useForm<UpdateVesselCallForm>({
    defaultValues: {
      eta: vesselCallDetails?.eta ?? "",
      expectedArrivalLocation: vesselCallDetails?.expectedArrivalLocation ?? "",
    },
  });

  const expectedArrivalLocationOptions = useMemo(
    () =>
      ports
        .find((p) => p.portCode === vesselCallDetails?.selectPortReferenceCode)
        ?.fromToLocations?.sort((a, b) => a.locationName.localeCompare(b.locationName))
        .map((l) => ({ label: l.locationName, value: l.fromToLocationCode })) || [],
    [ports, vesselCallDetails?.selectPortReferenceCode],
  );

  const updateVesselCallMutation = useMutation({
    mutationFn: (payload: UpdateVesselCallForm) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "UpdateVesselCall",
          action: "update_vessel_call",
          entityType: "vessel_call",
        },
        (transactionId) =>
          updateVesselCallById({
            service: "bff-vessel-call",
            vesselCallId: vesselCallDetails?.id || "",
            payload,
            ...(transactionId ? { transactionId } : {}),
          }),
      ),
    onSuccess: async (_, variables) => {
      notify({
        title: t(`${LANG_BASE}.success-title`),
        description: t(`${LANG_BASE}.success-description`),
        type: "success",
      });
      await refetchVesselCall();
      queryClient.invalidateQueries({ queryKey: ["vesselCall"] });
      queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallDetails?.id],
      });
      onUpdateSubmit?.(variables);
    },
    onError: (error: any) => {
      notify({
        title: t(`${LANG_BASE}.error-title`),
        description:
          error?.response?.data?.detail ?? error?.message ?? t(`${LANG_BASE}.error-description`),
        type: "error",
      });
    },
  });

  const canSubmit = form.watch("eta") || form.watch("expectedArrivalLocation");

  const { handleSubmit } = form;

  const onSubmit = handleSubmit((formData) => {
    if (!canSubmit) {
      notify({
        title: t(`${LANG_BASE}.error-title`),
        description: t(`${LANG_BASE}.no-changes-description`),
        type: "error",
      });
      return;
    }

    const expiredCertificates = validateVesselCertificates(
      formData.expectedArrivalLocation,
      vesselRegisterDetails,
      vesselRegisterDetails?.diplomaticVessel,
    );

    if (expiredCertificates.length > 0) {
      notify({
        title: t("vessel-call.create.notifications.certificate-validation-title"),
        description: t("vessel-call.create.notifications.certificate-validation-description"),
        type: "error",
      });
      return;
    }

    const payload: UpdateVesselCallPayload = {
      ...(formData.eta ? { eta: formData.eta } : {}),
      ...(formData.expectedArrivalLocation
        ? { expectedArrivalLocation: formData.expectedArrivalLocation }
        : {}),
    };

    updateVesselCallMutation.mutate(payload);
  });

  return (
    <FormProvider {...form}>
      <div className="flex justify-center mt-4 mb-8">
        <div
          className="w-full rounded-lg overflow-hidden"
          style={{
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
          }}
          data-testid="update-vessel-call-section"
        >
          <div className="py-3 px-5" style={{ background: theme.palette.grey[100] }}>
            <span className="text-sm font-extrabold" style={{ color: theme.palette.text.primary }}>
              {t(`${LANG_BASE}.title`)}
            </span>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
              <div className="w-full">
                <FormItem<UpdateVesselCallForm, "expectedArrivalLocation">
                  name="expectedArrivalLocation"
                  type="select"
                  options={expectedArrivalLocationOptions}
                  label={t(`${LANG_BASE}.expected-arrival-location`)}
                  testId="expected-arrival-location"
                />
              </div>

              <div className="w-full">
                <FormItem<UpdateVesselCallForm, "eta">
                  name="eta"
                  type="date-picker"
                  label={t(`${LANG_BASE}.eta`)}
                  testId="eta"
                  dateConstraint="not-past"
                  combinedInput={true}
                />
              </div>

              <div className="w-full flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  testId="cancel-update-vessel-call"
                  onClick={onCancel}
                  label={<span className="normal-case font-medium">{t("common.cancel")}</span>}
                />
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  testId="submit-update-vessel-call"
                  onClick={onSubmit}
                  label={
                    <span className="normal-case font-medium">
                      <ContentPasteGo className="mr-2" fontSize="small" />
                      {t(`${LANG_BASE}.submit-changes`)}
                    </span>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

export default UpdateVesselCallSection;
